import {
  Activity,
  OtherTypes,
  Purpose,
  RateUnitPeriod,
  UnitOfTime,
  VehicleProperties,
  VehicleTypes,
} from "@/types/policy.enum";
import { ColorRgba, Policy, PolicyRuleInput, RateInput } from "types";
import { HeaderItem } from "components/data-table/types/table.types";
import { PresetPolicy, colors } from "constants/map.const";

import { IconColor, PolicyCategory, SortOrder } from "@/types/gql/graphql";
import { RgbaColor } from "react-colorful";
import { SortOption } from "./components/PolicyTable";
import { formatArrayToRgbaColor } from "helper/utils";

export const defaultRule: PolicyRuleInput = {
  activity: Activity.parking,
  maxStayUnit: UnitOfTime.Minute,
  noReturnUnit: UnitOfTime.Minute,
  rate: [],
};

export const defaultPolicy: Policy = {
  cityId: "",
  name: "",
  priority: 1,
  rules: [defaultRule],
  timeSpans: [],
  dataSourceOperatorId: [],
  publishedDate: new Date(),
};

export const arrActivity = Object.values(Activity);

export const arrUnitOfTime = Object.values(UnitOfTime);

export const arrRateUnitPeriod = Object.values(RateUnitPeriod);

export const defaultRate: RateInput = {
  rate: 0,
  rateUnit: UnitOfTime.Minute,
};

export const arrVehicleTypes = Object.values(VehicleTypes);
export const arrVehicleProperties = Object.values(VehicleProperties);
export const arrPurpose = Object.values(Purpose);

export const arrUserClasses = [...arrVehicleTypes, ...arrVehicleProperties, ...arrPurpose];

export const defaultFillColorRgba: RgbaColor = {
  r: 70,
  g: 127,
  b: 233,
  a: 56,
};
export const defaultLineColorRgba: RgbaColor = {
  r: 70,
  g: 127,
  b: 233,
  a: 255,
};

export const defaultFillColor: ColorRgba = [
  defaultFillColorRgba.r,
  defaultFillColorRgba.g,
  defaultFillColorRgba.b,
  defaultFillColorRgba.a,
];
export const defaultLineColor: ColorRgba = [
  defaultLineColorRgba.r,
  defaultLineColorRgba.g,
  defaultLineColorRgba.b,
  defaultLineColorRgba.a,
];

export type PresetColorByPolicy = {
  title: PresetPolicy;
  rgba: RgbaColor;
};

export type PresetColor = {
  fillColors: PresetColorByPolicy[];
  lineColors: PresetColorByPolicy[];
};

export const presetPolicyColors = Object.entries(colors).reduce(
  (preValue: PresetColor, item) => {
    const newFillColor = formatArrayToRgbaColor(item[1].fillColor);
    const newLineColor = formatArrayToRgbaColor(item[1].lineColor);
    const title = item[0];

    return {
      fillColors: [...preValue.fillColors, { title, rgba: newFillColor }],
      lineColors: [...preValue.lineColors, { title, rgba: newLineColor }],
    } as PresetColor;
  },
  { fillColors: [], lineColors: [] },
);

export type PolicyValue = {
  priority: number;
  timeOfDayStart?: string;
  timeOfDayEnd?: string;
  userClasses?: string[];
  maxStay?: number;
  maxStayUnit?: UnitOfTime;
  activity: Activity;
};

export const arrCategory: { label: string; key: PolicyCategory; value: PolicyValue | null }[] = [
  {
    label: "Autres",
    key: PolicyCategory.Other,
    value: null,
  },
  {
    label: "Payant",
    key: PolicyCategory.Paid,
    value: null,
  },
  {
    label: "Zone bleue",
    key: PolicyCategory.Disc,
    value: null,
  },
  {
    label: "Trottinette",
    key: PolicyCategory.Scooter,
    value: null,
  },
  {
    label: "Taxi",
    key: PolicyCategory.Taxi,
    value: null,
  },
  {
    label: "Gratuit",
    key: PolicyCategory.Free,
    value: {
      priority: 2,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      userClasses: [VehicleTypes.car],
      activity: Activity.parking,
    },
  },
  {
    label: "Police",
    key: PolicyCategory.Police,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      userClasses: [OtherTypes.police],
      activity: Activity.parking,
    },
  },
  {
    label: "Livraison",
    key: PolicyCategory.Delivery,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      maxStay: 30,
      maxStayUnit: UnitOfTime.Minute,
      userClasses: [Purpose.delivery],
      activity: Activity.parking,
    },
  },
  {
    label: "Moto",
    key: PolicyCategory.Motorcycle,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      userClasses: [VehicleTypes.motorcycle],
      activity: Activity.parking,
    },
  },
  {
    label: "Électrique",
    key: PolicyCategory.Electric,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      userClasses: [VehicleProperties.electric],
      activity: Activity.parking,
    },
  },
  {
    label: "PMR",
    key: PolicyCategory.Disabled,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      userClasses: [VehicleTypes.car, VehicleProperties.HandicapAccessible],
      activity: Activity.parking,
    },
  },
  {
    label: "Vélo",
    key: PolicyCategory.Bicycle,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      userClasses: [VehicleTypes.bicycle, VehicleTypes.bike],
      activity: Activity.parking,
    },
  },
  {
    label: "Vélo partage",
    key: PolicyCategory.BikeSharing,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      userClasses: [VehicleTypes.bicycle, VehicleTypes.bike],
      activity: Activity.parking,
    },
  },
  {
    label: "Deux Roues",
    key: PolicyCategory.TwoWheels,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      userClasses: [VehicleTypes.scooter, OtherTypes.twoWheeledVehicles, VehicleTypes.motorbike, OtherTypes.twoWheeler],
      activity: Activity.parking,
    },
  },
  {
    label: "Transport de fond",
    key: PolicyCategory.CashTransport,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      userClasses: [OtherTypes.cashEscort, OtherTypes.cashTransport, OtherTypes.moneyTransporter],
      activity: Activity.parking,
    },
  },
  {
    label: "Autocar",
    key: PolicyCategory.Coach,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      userClasses: [VehicleTypes.bus, PolicyCategory.Coach, VehicleTypes.autobus],
      activity: Activity.parking,
    },
  },
  {
    label: "Pompiers",
    key: PolicyCategory.FireFighters,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "23:59",
      userClasses: [OtherTypes.pompiers, OtherTypes.firefighter],
      activity: Activity.parking,
    },
  },
  {
    label: "Ambulance",
    key: PolicyCategory.Ambulance,
    value: {
      priority: 1,
      timeOfDayStart: "00:00",
      timeOfDayEnd: "24:00",
      userClasses: [Purpose.emergencyUse],
      activity: Activity.parking,
    },
  },
  {
    label: "Auto-partage",
    key: PolicyCategory.Carpooling,
    value: null,
  },
  {
    label: "Arrêt de bus",
    key: PolicyCategory.BusStop,
    value: null,
  },
  {
    label: "Dépose minute",
    key: PolicyCategory.DropOff,
    value: null,
  },
];

export const [defaultCategory] = arrCategory.filter((cat) => cat.key === PolicyCategory.Other);

export const arrCategoryDropdown = arrCategory.map((cat) => ({ value: cat.key, label: cat.label }));

export const defaultSort: SortOption = {
  orderBy: "publishedDate",
  direction: SortOrder.Desc,
};

export const defaultCells: HeaderItem<keyof Policy>[] = [
  {
    title: "Nom",
    orderBy: "name",
    sortable: true,
    sortDirection: SortOrder.Asc,
    // 1078px = sum of the fixed column widths below (3×w-45 + 2×w-36 + w-40 + w-22.5); keep in sync
    className: "w-auto max-w-[calc(100%-1078px)] break-words whitespace-normal",
  },
  {
    title: "Date de publication",
    orderBy: "publishedDate",
    sortable: true,
    sortDirection: SortOrder.Desc,
    className: "w-45",
  },
  {
    title: "Catégorie",
    orderBy: "category",
    sortable: true,
    sortDirection: SortOrder.Asc,
    className: "w-45",
  },
  {
    title: "Durée max",
    className: "w-36",
  },
  {
    title: "Jours",
    className: "w-36",
  },
  {
    title: "Horaires",
    className: "w-40",
  },
  {
    title: "Priorité",
    className: "w-22.5 text-right",
    orderBy: "priority",
    sortable: true,
    sortDirection: SortOrder.Asc,
  },
  { title: "Action", className: "text-center w-45" },
];

// Fixed list of allowed icon colors for policy icons
export const iconColorOptions: Record<IconColor, string> = {
  green: "#71C045",
  blue: "#0000FF",
  red: "#FF0000",
  orange1: "#AD562A",
  orange2: "#F8951E",
  purple: "#9C83F6",
  yellow: "#F7FF00",
};

export const defaultBackgroundIcon = "#FFFFFF";

export type PresetIconColor = {
  label: string;
  color: string;
};
